/**
 * LP PDP Section - Toggle functionality between Subscribe and One-Time purchase
 */

class LpPdpSection {
  constructor(container) {
    this.container = container;
    this.toggleButtons = container.querySelectorAll('.lp-pdp-toggle-btn');
    this.subscriptionSection = container.querySelector('.lp-pdp-subscription-section');
    this.onetimeSection = container.querySelector('.lp-pdp-onetime-section');
    this.subscriptionOptions = container.querySelectorAll('.lp-pdp-option');
    this.form = container.querySelector('.lp-pdp-form');
    this.productId = container.querySelector('[data-product-id]')?.dataset.productId;
    this.quantityInput = container.querySelector('.lp-pdp-quantity-input');
    this.propertyInput = container.querySelector('.lp-pdp-property-input');
    this.init();
  }

  init() {
    if (!this.toggleButtons.length) return;

    // Add click handlers to toggle buttons
    this.toggleButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleToggle(e));
    });

    // Add click handlers to subscription options
    this.subscriptionOptions.forEach(option => {
      option.addEventListener('click', (e) => this.handleOptionClick(e));
    });

  }

  handleOptionClick(e) {
    const clickedOption = e.currentTarget;
    const radioInput = clickedOption.querySelector('.lp-pdp-option-radio-input');

    // Get the parent section to only update options within the same section
    const parentSection = clickedOption.closest('.lp-pdp-subscription-section, .lp-pdp-onetime-section');
    const optionsInSection = parentSection.querySelectorAll('.lp-pdp-option');

    // Remove selected class from all options in this section
    optionsInSection.forEach(opt => opt.classList.remove('selected'));

    // Add selected class to clicked option
    clickedOption.classList.add('selected');

    // Check the radio input
    if (radioInput) {
      radioInput.checked = true;
    }

    // Update quantity based on selected option
    this.updateQuantity(clickedOption);

    // Update line item property for 3 products subscription
    const isSubscription = parentSection.classList.contains('lp-pdp-subscription-section');
    this.updateLineItemProperty(clickedOption, isSubscription);

    // Update the savings amount for both subscription and one-time
    this.updateSavingsDisplay(clickedOption, parentSection);
  }

  updateQuantity(selectedOption) {
    const quantity = selectedOption.dataset.quantity;
    if (this.quantityInput && quantity) {
      this.quantityInput.value = quantity;
    }

    // Update supply image visibility
    this.updateSupplyImage(quantity);
  }

  updateLineItemProperty(selectedOption, isSubscription) {
    const quantity = selectedOption.dataset.quantity;

    if (this.propertyInput) {
      // Set property to "true" only if it's a subscription with 3 products
      if (isSubscription && quantity === '3') {
        this.propertyInput.value = 'true';
        this.propertyInput.disabled = false;
      } else {
        // Disable the input so it won't be submitted with the form
        this.propertyInput.disabled = true;
      }
    }
  }

  updateSupplyImage(quantity) {
    // Supply image visibility is now handled by CSS transition
    // on the parent .lp-pdp-option.selected wrapper
  }

  updateSavingsDisplay(selectedOption, parentSection) {
    const savingsAmount = selectedOption.dataset.savings;
    const savingsContainer = parentSection ? parentSection.querySelector('.lp-pdp-savings') : null;
    const savingsElement = savingsContainer ? savingsContainer.querySelector('.lp-pdp-savings-amount') : null;

    if (savingsContainer && savingsElement && savingsAmount !== undefined) {
      const savingsValue = parseFloat(savingsAmount);
      if (savingsValue > 0) {
        const formattedSavings = this.formatMoney(savingsAmount);
        savingsElement.textContent = formattedSavings;
        savingsContainer.style.display = '';
      } else {
        savingsContainer.style.display = 'none';
      }
    }
  }

  formatMoney(cents) {
    // Get the currency format from Shopify theme settings
    const amount = (parseFloat(cents) / 100).toFixed(2);

    // Try to get currency format from existing price elements
    const priceElement = this.container.querySelector('.lp-pdp-option-price');
    if (priceElement) {
      const priceText = priceElement.textContent.trim();
      // Extract currency symbol and format
      const currencyMatch = priceText.match(/^([^\d]+)/);
      const currencySymbol = currencyMatch ? currencyMatch[1] : 'CHF ';
      return `${currencySymbol}${amount}`;
    }

    // Fallback format
    return `CHF ${amount}`;
  }

  handleToggle(e) {
    const button = e.currentTarget;
    const toggleType = button.dataset.toggle;

    // Update active state on buttons
    this.toggleButtons.forEach(btn => btn.classList.remove('active'));
    button.classList.add('active');

    // Show/hide appropriate sections
    if (toggleType === 'subscribe') {
      this.showSubscriptionSection();
    } else {
      this.showOnetimeSection();
    }

  }

  showSubscriptionSection() {
    if (this.subscriptionSection) {
      this.subscriptionSection.style.display = 'block';
    }
    if (this.onetimeSection) {
      this.onetimeSection.style.display = 'none';
    }

    // Restore selected subscription option state
    // const selectedSubscriptionOption = this.subscriptionSection.querySelector('.lp-pdp-option.selected');
    // if (selectedSubscriptionOption) {
    //   const radioInput = selectedSubscriptionOption.querySelector('input[name="selling_plan"]');
    //   if (radioInput) {
    //     radioInput.checked = true;
    //   }
    //   this.updateQuantity(selectedSubscriptionOption);
    //   this.updateLineItemProperty(selectedSubscriptionOption, true);
    // }
  }

  showOnetimeSection() {
    if (this.subscriptionSection) {
      this.subscriptionSection.style.display = 'none';
    }
    if (this.onetimeSection) {
      this.onetimeSection.style.display = 'block';
    }

    // Clear subscription selection
    if (this.form) {
      const sellingPlanInputs = this.form.querySelectorAll('input[name="selling_plan"]');
      sellingPlanInputs.forEach(input => {
        input.checked = false;
      });
    }

    // Set quantity based on selected one-time option
    const selectedOnetimeOption = this.onetimeSection.querySelector('.lp-pdp-option.selected');
    if (selectedOnetimeOption) {
      this.updateQuantity(selectedOnetimeOption);
      this.updateLineItemProperty(selectedOnetimeOption, false);
    }
  }
}

// Initialize all LP PDP sections on the page
function initLpPdpSections() {
  const sections = document.querySelectorAll('.lp-pdp-product-selector');
  sections.forEach(section => {
    new LpPdpSection(section);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLpPdpSections);
} else {
  initLpPdpSections();
}

// Re-initialize on Shopify section load (for theme editor)
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.classList.contains('lp-pdp-section-class')) {
    initLpPdpSections();
  }
});

